import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { resetPassword } from '../../services/api';
import { applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const tenantSlug = searchParams.get('tenant');

  const [form, setForm]     = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(false);
  const [branding, setBranding] = useState(null); // { name, logo_url, primary_color }

  const loginHref = `/login${tenantSlug ? `?tenant=${tenantSlug}` : ''}`;

  useEffect(() => {
    if (!token) {
      setError('Link inválido. Solicite um novo link de redefinição de senha.');
    }
  }, [token]);

  // Load tenant branding when ?tenant=slug is in the URL
  useEffect(() => {
    if (!tenantSlug) return;
    axios.get(`${API_URL}/public/${tenantSlug}`)
      .then(r => {
        const t = r.data.tenant;
        if (t) setBranding({ name: t.name, logo_url: t.logo_url, primary_color: t.primary_color || '#2563eb' });
      })
      .catch(() => {});
  }, [tenantSlug]);

  // Apply tenant branding (primary + dark variação gerada automaticamente)
  useEffect(() => {
    if (branding?.primary_color) {
      applyTenantBranding(branding.primary_color);
    }
    return () => clearTenantBranding();
  }, [branding]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!form.password) { setError('Informe a nova senha'); return; }
    if (form.password.length < 8) { setError('A senha deve ter no mínimo 8 caracteres'); return; }
    if (form.password !== form.confirm) { setError('As senhas não coincidem'); return; }

    setLoading(true);
    try {
      await resetPassword(token, form.password);
      setDone(true);
      toast.success('Senha redefinida com sucesso!');
      setTimeout(() => navigate(loginHref, { replace: true }), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao redefinir a senha. Tente novamente.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          {branding ? (
            <>
              {branding.logo_url
                ? <img src={branding.logo_url} alt={branding.name} style={{ height:64, objectFit:'contain', marginBottom:10, borderRadius:8 }} />
                : <div style={{ width:64, height:64, background:'var(--primary)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'white', fontSize:26, margin:'0 auto 10px' }}>
                    {branding.name?.[0]?.toUpperCase() || 'A'}
                  </div>
              }
              <h1>{branding.name}</h1>
              <p>Redefinir senha</p>
            </>
          ) : (
            <>
              <img src="/logo-full.svg" alt="AtendoApp" style={{ height:48, objectFit:'contain', marginBottom:10 }} />
              <p>Plataforma de agendamentos SaaS</p>
            </>
          )}
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div className="success-box" style={{ marginBottom: 24, textAlign: 'left' }}>
              <div style={{ fontSize: 32, marginBottom: 8, textAlign: 'center' }}>✅</div>
              <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 15 }}>Senha redefinida!</p>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
                Sua senha foi alterada com sucesso. Você será redirecionado para o login em instantes.
              </p>
            </div>
            <Link
              to={loginHref}
              style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            >
              Ir para o login agora →
            </Link>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 24, lineHeight: 1.6 }}>
              Digite sua nova senha abaixo. Use pelo menos 8 caracteres.
            </p>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="error-box" style={{ marginBottom: 16 }}>⚠️ {error}</div>
              )}

              <div className="form-group">
                <label>Nova senha</label>
                <input
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  autoFocus
                  autoComplete="new-password"
                  disabled={loading || !token}
                />
              </div>

              <div className="form-group">
                <label>Confirmar nova senha</label>
                <input
                  type="password"
                  placeholder="Repita a nova senha"
                  value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                  autoComplete="new-password"
                  disabled={loading || !token}
                />
              </div>

              <button
                type="submit"
                className={`btn btn-primary${loading ? ' btn-loading' : ''}`}
                style={{ width: '100%', padding: 11, fontSize: 14, marginTop: 4 }}
                disabled={loading || !token}
              >
                {loading ? 'Salvando...' : 'Redefinir senha'}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13 }}>
              <Link
                to={loginHref}
                style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
              >
                ← Voltar ao login
              </Link>
            </p>
          </>
        )}

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--gray-400)' }}>
          {branding ? `Desenvolvido por AtendoApp` : `AtendoApp © ${new Date().getFullYear()}`}
        </p>
      </div>
    </div>
  );
}
